import React from "react";
import { Input, InputProps } from "@fluentui/react-components";
import {
  formatRuleAmount,
  getCurrencySymbol,
  isPercentageAmount,
} from "../../utils/currency";

/**
 * The amount input of a commission rule on Add/Edit screens.
 *
 * At rest it shows the amount exactly as View does — "50.00%" for a Percent
 * rule, the company currency at the configured Decimal Places for a Money rule.
 * An `<input type="number">` cannot hold either string, so on focus it hands
 * over to a plain number input holding the raw value, and returns to the
 * formatted view on blur. Only the display changes: the value it receives and
 * reports through `onChange` stays the raw number, so Formik state and API
 * payloads are untouched.
 */
const RuleAmountInput = React.forwardRef<
  HTMLInputElement,
  InputProps & { amountFlag: unknown }
>(
  (
    { amountFlag, value, onFocus, onBlur, contentBefore, contentAfter, ...rest },
    ref,
  ) => {
    const [focused, setFocused] = React.useState(false);
    const raw = value ?? "";
    const formatted = formatRuleAmount(raw, amountFlag);
    const isPercent = isPercentageAmount(amountFlag);
    // An empty amount has nothing to format, so it stays a normal input.
    const showFormatted = !focused && formatted !== "";

    return (
      <Input
        {...rest}
        ref={ref}
        type={showFormatted ? "text" : "number"}
        value={showFormatted ? formatted : String(raw)}
        contentBefore={
          showFormatted || isPercent ? null : <strong>{getCurrencySymbol()}</strong>
        }
        contentAfter={!showFormatted && isPercent ? <strong>%</strong> : null}
        onFocus={(e) => {
          setFocused(true);
          onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          onBlur?.(e);
        }}
      />
    );
  },
);

export default RuleAmountInput;
